import { useMemo, useState } from 'react'
import { adminRequest } from './EshopCategories'

/**
 * Import zákazníkov z Money S4 (alebo akéhokoľvek CSV exportu). Súbor sa parsuje priamo
 * v prehliadači – administrátor priradí stĺpce k poliam, pozrie si náhľad a potvrdí import.
 * Páruje sa podľa e-mailu: existujúci zákazník sa doplní, nový sa založí (heslo si nastaví
 * cez odkaz na obnovu – pošle sa mu neskôr z karty zákazníka).
 */

type FieldKey = 'name' | 'email' | 'phone' | 'companyName' | 'companyIco' | 'companyDic' | 'companyIcDph' | 'billingAddress' | 'discountPercent'
const FIELDS: Array<{ key: FieldKey; label: string; required?: boolean }> = [
  { key: 'name', label: 'Meno / názov', required: true },
  { key: 'email', label: 'E-mail', required: true },
  { key: 'phone', label: 'Telefón' },
  { key: 'companyName', label: 'Firma' },
  { key: 'companyIco', label: 'IČO' },
  { key: 'companyDic', label: 'DIČ' },
  { key: 'companyIcDph', label: 'IČ DPH' },
  { key: 'billingAddress', label: 'Fakturačná adresa' },
  { key: 'discountPercent', label: 'Zľava (%)' },
]

/** Jednoduchý CSV parser (RFC4180: úvodzovky, čiarka alebo bodkočiarka, BOM, CRLF). */
export function parseCsv(text: string): string[][] {
  const clean = text.replace(/^﻿/, '')
  const delimiter = (clean.slice(0, 2000).match(/;/g)?.length ?? 0) > (clean.slice(0, 2000).match(/,/g)?.length ?? 0) ? ';' : ','
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]
    if (inQuotes) {
      if (ch === '"') { if (clean[i + 1] === '"') { field += '"'; i++ } else inQuotes = false }
      else field += ch
    } else if (ch === '"') inQuotes = true
    else if (ch === delimiter) { row.push(field); field = '' }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && clean[i + 1] === '\n') i++
      row.push(field); field = ''
      if (row.some(f => f.trim() !== '')) rows.push(row)
      row = []
    } else field += ch
  }
  if (field !== '' || row.length) { row.push(field); if (row.some(f => f.trim() !== '')) rows.push(row) }
  return rows
}

/**
 * Jednoduché a tolerantné parsovanie XML exportu (napr. z Money S4). Nevyžaduje presnú znalosť
 * schémy – nájde najčastejšie opakujúci sa element (jeden zákazník = jeden taký element) a jeho
 * priame podelementy a atribúty použije ako stĺpce, presne ako pri CSV (vrátane priraďovania).
 */
export function parseXml(text: string): string[][] {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.querySelector('parsererror')) return []
  const all = Array.from(doc.getElementsByTagName('*'))
  const counts = new Map<string, Element[]>()
  for (const el of all) { const list = counts.get(el.tagName) ?? []; list.push(el); counts.set(el.tagName, list) }
  // Riadok = element, ktorého značka sa opakuje najviac (aspoň 2×) a súčasne má podelementy alebo atribúty.
  let rowTag = ''; let rowEls: Element[] = []
  for (const [tag, els] of counts) {
    if (els.length < 2) continue
    const hasData = els[0].attributes.length > 0 || els[0].children.length > 0
    if (hasData && els.length > rowEls.length) { rowTag = tag; rowEls = els }
  }
  if (!rowTag) return []
  const headerOrder: string[] = []
  const seen = new Set<string>()
  const rows = rowEls.map(el => {
    const fields: Record<string, string> = {}
    for (const attr of Array.from(el.attributes)) fields[attr.name] = attr.value.trim()
    for (const child of Array.from(el.children)) {
      const key = child.tagName
      if (child.children.length === 0) fields[key] = (child.textContent ?? '').trim()
      else if (fields[key] === undefined) fields[key] = (child.textContent ?? '').trim()
    }
    for (const key of Object.keys(fields)) if (!seen.has(key)) { seen.add(key); headerOrder.push(key) }
    return fields
  })
  return [headerOrder, ...rows.map(r => headerOrder.map(h => r[h] ?? ''))]
}

const isXml = (file: File, text: string) => file.name.toLowerCase().endsWith('.xml') || file.type.includes('xml') || text.trimStart().startsWith('<')

const guessMap = (headers: string[]): Partial<Record<FieldKey, number>> => {
  const norm = (s: string) => s.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')
  const find = (...needles: string[]) => headers.findIndex(h => needles.some(n => norm(h).includes(n)))
  const map: Partial<Record<FieldKey, number>> = {}
  const set = (key: FieldKey, idx: number) => { if (idx >= 0) map[key] = idx }
  set('email', find('email', 'mail'))
  set('name', find('nazov', 'meno', 'obchodny nazov', 'name'))
  set('phone', find('telefon', 'mobil', 'phone'))
  set('companyName', find('firma', 'spolocnost', 'company'))
  set('companyIco', find('ico'))
  set('companyIcDph', find('icdph', 'ic dph', 'vat'))
  set('companyDic', find('dic'))
  set('billingAddress', find('adresa', 'ulica', 'address'))
  set('discountPercent', find('zlava', 'discount'))
  return map
}

export default function CustomerImport({ onClose, onImported }: { onClose: () => void; onImported: (summary: { created: number; updated: number; skipped: Array<{ row: number; name: string; reason: string }> }) => void }) {
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [map, setMap] = useState<Partial<Record<FieldKey, number>>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function onFile(file: File) {
    setError(''); setFileName(file.name)
    const text = await file.text()
    const xml = isXml(file, text)
    const parsed = xml ? parseXml(text) : parseCsv(text)
    if (!parsed.length) { setError(xml ? 'V XML súbore sa nenašiel opakujúci sa záznam zákazníka (skontrolujte export z Money S4).' : 'Súbor je prázdny alebo sa nepodarilo prečítať.'); return }
    const [head, ...body] = parsed
    setHeaders(head); setRows(body); setMap(guessMap(head))
  }

  const mappedRows = useMemo(() => rows.map(r => Object.fromEntries(FIELDS.map(f => [f.key, map[f.key] !== undefined ? (r[map[f.key]!] ?? '').trim() : '']))), [rows, map])
  const readyRows = useMemo(() => mappedRows.filter(r => r.name && r.email), [mappedRows])
  const missing = mappedRows.length - readyRows.length

  async function doImport() {
    if (!readyRows.length || saving) return
    setSaving(true); setError('')
    try {
      const payload = readyRows.map(r => ({
        name: r.name, email: r.email,
        ...(r.phone ? { phone: r.phone } : {}),
        ...(r.companyName ? { companyName: r.companyName } : {}),
        ...(r.companyIco ? { companyIco: r.companyIco } : {}),
        ...(r.companyDic ? { companyDic: r.companyDic } : {}),
        ...(r.companyIcDph ? { companyIcDph: r.companyIcDph } : {}),
        ...(r.billingAddress ? { billingAddress: r.billingAddress } : {}),
        ...(r.discountPercent && Number.isFinite(Number(r.discountPercent)) ? { discountPercent: Math.max(0, Math.min(90, Math.round(Number(r.discountPercent)))) } : {}),
      }))
      const data = await adminRequest('/api/admin/customers/import', { method: 'POST', body: JSON.stringify({ rows: payload }) })
      onImported(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import sa nepodaril.')
    } finally { setSaving(false) }
  }

  return <div className="modal-backdrop" onClick={onClose}>
    <section className="order-detail ci-panel" role="dialog" aria-modal="true" aria-labelledby="ci-title" onClick={e => e.stopPropagation()}>
      <div className="panel-heading"><h2 id="ci-title">Import zákazníkov z Money S4</h2><button type="button" className="close-btn" aria-label="Zavrieť" onClick={onClose}>×</button></div>

      {!headers.length && <div className="ci-upload">
        <p>V Money S4 exportujte zákazníkov do súboru CSV (alebo uložte Excel export ako „CSV UTF-8“) – prijme sa aj XML export.</p>
        <label className="ci-file-btn">Vybrať súbor…<input type="file" accept=".csv,text/csv,.txt,.xml,text/xml,application/xml" onChange={e => { const f = e.target.files?.[0]; if (f) void onFile(f) }} /></label>
        {error && <p className="error-message">{error}</p>}
      </div>}

      {headers.length > 0 && <>
        <p className="ci-filename">Súbor <strong>{fileName}</strong> · {rows.length} riadkov</p>
        <h3>Priraďte stĺpce</h3>
        <div className="ci-map-grid">{FIELDS.map(f => <label key={f.key}>{f.label}{f.required ? ' *' : ''}
          <select value={map[f.key] ?? ''} onChange={e => setMap(current => ({ ...current, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) }))}>
            <option value="">— nepoužiť —</option>
            {headers.map((h, i) => <option key={i} value={i}>{h || `Stĺpec ${i + 1}`}</option>)}
          </select>
        </label>)}</div>

        <h3>Náhľad ({readyRows.length} pripravených{missing > 0 ? `, ${missing} vynechaných – chýba meno alebo e-mail` : ''})</h3>
        <div className="ci-preview-wrap"><table className="ci-preview"><thead><tr>{FIELDS.map(f => <th key={f.key}>{f.label}</th>)}</tr></thead>
          <tbody>{mappedRows.slice(0, 8).map((r, i) => <tr key={i} className={!r.name || !r.email ? 'is-missing' : ''}>{FIELDS.map(f => <td key={f.key}>{r[f.key] || '—'}</td>)}</tr>)}</tbody>
        </table>{mappedRows.length > 8 && <p className="ci-more">…a ďalších {mappedRows.length - 8} riadkov</p>}</div>

        {error && <p className="error-message">{error}</p>}
        <div className="ci-actions">
          <button type="button" className="pa-ghost" onClick={() => { setHeaders([]); setRows([]); setMap({}) }}>Vybrať iný súbor</button>
          <button type="button" className="primary-btn" disabled={!readyRows.length || saving} onClick={() => void doImport()}>{saving ? 'Importujem…' : `Importovať ${readyRows.length} zákazníkov`}</button>
        </div>
      </>}
    </section>
  </div>
}
